<template>
  <v-container>
    <v-row>
      <ViewHeader :title="$t('message.reviews.analyst.title')">
        <div class="filters" v-if="isOwner">
          <ui-button
            color="primary"
            class="mr-6"
            icon="icon-filter"
            @click="filterDialog = true"
          >
            {{ $t("message.reviews.filter") }}
          </ui-button>
          <ReviewsAnalystDateRangeFilter
            :first-date.sync="periods.first"
            @update:first-date="periods.first = $event"
            @change="onFiltersChange({ periods: $event })"
          />
        </div>
      </ViewHeader>
    </v-row>

    <v-row>
      <div class="reviews-filter-alert">
        {{ $t("message.reviews.analyst.includedRatings") }}:
        {{ $t(`message.reviews.analyst.${includedRatingsKey}`) }}
      </div>
    </v-row>

    <template v-if="isOwner">
      <v-row class="summary">
        <v-col lg="4" cols="12">
          <ReviewsAnalystSummary :stats="stats" :loading="statsLoading" />
        </v-col>
        <v-col lg="8" cols="12">
          <ReviewsAnalystRatingsChart :stats="stats" :loading="statsLoading" />
        </v-col>
      </v-row>

      <v-row class="comparison">
        <v-col>
          <ReviewsAnalystComparisonTable
            ref="reviewsAnalystComparisonTable"
            v-bind="queryParams"
          />
        </v-col>
      </v-row>

      <FilterDialog
        v-if="filterDialog"
        :gmb-addresses-ids="selectedGmbAddressesIds"
        :cities="selectedCities"
        :facilities-groups="facilitiesGroupsIds"
        :show-comments-options="true"
        :chosen-search-type="searchType"
        :chosen-only-comments="onlyComments"
        :chosen-only-accurate="onlyAccurate"
        :chosen-only-updated="onlyUpdated"
        :chosen-only-created="onlyCreated"
        show-date-options
        @setSearchType="searchType = $event"
        @setOnlyComments="onlyComments = $event"
        @setOnlyAccurate="onlyAccurate = $event"
        @setOnlyUpdated="onlyUpdated = $event"
        @setOnlyCreated="onlyCreated = $event"
        @change="onFiltersChange({ ...$event })"
        :handleClose="closeFilterDialog"
      />
    </template>

    <v-card style="min-height: 650px;" v-else class="d-flex px-12">
      <div class="mx-auto d-flex align-center">
        <div class="no-data d-flex flex-column align-center">
          <h2 class="subtitle mb-6">
            {{ $t("message.reviews.analyst.noFacilities.title") }}
          </h2>
          <p class="no-data__description text-center">
            {{ $t("message.reviews.analyst.noFacilities.desc") }}
          </p>
          <v-btn color="secondary" @click="goToGMBAccounts">
            {{ $t("message.reviews.noFacilities.button") }}
          </v-btn>
        </div>
      </div>
      <SectionLoader :status="loading" :opacity="1" />
    </v-card>
  </v-container>
</template>

<script>
import ReviewsAnalystSummary from "@/components/Reviews/Analyst/ReviewsAnalystSummary";
import ReviewsAnalystRatingsChart from "@/components/Reviews/Analyst/ReviewsAnalystRatingsChart";
import ReviewsAnalystComparisonTable from "@/components/Reviews/Analyst/ReviewsAnalystComparisonTable";
import ViewHeader from "@/components/ui/ViewHeader";
import FilterDialog from "@/components/Filter/dialog";
import { mapActions } from "vuex";

import moment from "moment";
import serviceHandler from "@/helpers/handlers/service";
import gmbReviewsService from "@/services/gmbReviews.js";
import { PERIOD } from "@/enums/period.enum";
import {
  REVIEWS_ACTION,
  REVIEWS_ACTION_TRANSLATION_KEY
} from "@/enums/reviews.enum";
import { FORMAT } from "@/config/date-format";
import { gtmEngagement as gtmEngagementMixin } from "@/helpers/mixins/gtm-engagement";
import { setCompanyIdFromQuery as setCompanyIdFromQueryMixin } from "@/helpers/mixins/set-company-id-from-query";
import ReviewsAnalystDateRangeFilter from "@/components/Reviews/Analyst/ReviewsAnalystDateRangeFilter";
import facilitiesService from "@/services/facilities";
import SectionLoader from "@/components/Loader/SectionLoader";

export default {
  name: "ReviewsAnalyst",
  components: {
    ReviewsAnalystSummary,
    ReviewsAnalystRatingsChart,
    ReviewsAnalystComparisonTable,
    ReviewsAnalystDateRangeFilter,
    FilterDialog,
    ViewHeader,
    SectionLoader
  },
  mixins: [gtmEngagementMixin, setCompanyIdFromQueryMixin],

  data() {
    return {
      loading: false,
      statsLoading: false,
      stats: {},
      selectedGmbAddressesIds: [],
      selectedCities: [],
      facilitiesGroupsIds: [],
      searchType: null,
      onlyComments: null,
      onlyAccurate: false,
      onlyUpdated: false,
      onlyCreated: false,
      filterDialog: false,
      periods: {
        first: [
          moment()
            .subtract(1, PERIOD.MONTH)
            .add(1, PERIOD.DAY)
            .format(FORMAT.DATE),
          moment().format(FORMAT.DATE)
        ],
        second: null
      },
      isOwner: false
    };
  },

  computed: {
    queryParams() {
      return {
        firstPeriod: this.periods.first || [],
        secondPeriod: this.periods.second || this.periods.first || [],
        gmbAddressesIds: this.selectedGmbAddressesIds || [],
        cities: this.selectedCities || [],
        groups: this.facilitiesGroupsIds || [],
        onlyComments: this.onlyComments,
        onlyAccurate: this.onlyAccurate,
        reviewAction: this.reviewAction
      };
    },
    reviewAction() {
      if (this.onlyUpdated) {
        return REVIEWS_ACTION.ONLY_UPDATED;
      } else if (this.onlyCreated) {
        return REVIEWS_ACTION.ONLY_CREATED;
      } else {
        return null;
      }
    },
    includedRatingsKey() {
      if (this.onlyUpdated) {
        return REVIEWS_ACTION_TRANSLATION_KEY.ONLY_UPDATED;
      }
      if (this.onlyCreated) {
        return REVIEWS_ACTION_TRANSLATION_KEY.ONLY_CREATED;
      }
      return REVIEWS_ACTION_TRANSLATION_KEY.ALL;
    }
  },

  async created() {
    this.loading = true;
    this.selectedGmbAddressesIds = this.$route.query.gmbAddressesIds
      ? this.$route.query.gmbAddressesIds.split(",").map(id => +id)
      : [];

    const response = await serviceHandler(
      facilitiesService.getFacilitiesData({
        page: 1,
        perPage: 1,
        is_owner: true
      })
    );

    if (response && response.count) {
      this.isOwner = true;
      this.setParams();
      this.getStats();
    } else {
      this.isOwner = false;
    }

    this.loading = false;
  },

  mounted() {
    this.mixin__setGtmEngagementTimeouts();
  },

  destroyed() {
    this.resetFacilitiesStore();
    this.resetFacilitiesGroupsStore();
  },

  methods: {
    ...mapActions({
      resetFacilitiesStore: "facilities/resetStore",
      resetFacilitiesGroupsStore: "facilitiesGroups/resetStore"
    }),

    closeFilterDialog() {
      this.filterDialog = false;
    },

    async getStats() {
      this.statsLoading = true;
      this.stats = await serviceHandler(
        gmbReviewsService.getStats({
          dates: this.queryParams.firstPeriod,
          gmbAddressIds: this.queryParams.gmbAddressesIds,
          cities: this.queryParams.cities,
          groups: this.queryParams.groups,
          comment: this.onlyComments,
          is_accurate: this.onlyAccurate || null,
          only_updated: this.onlyUpdated || null,
          only_created: this.onlyCreated || null
        })
      );
      this.statsLoading = false;
    },

    onFiltersChange({
      periods = this.periods,
      gmbAddressesIds = this.selectedGmbAddressesIds,
      cities = this.selectedCities,
      facilitiesGroupsIds = this.facilitiesGroupsIds
    }) {
      this.periods = periods;
      this.selectedGmbAddressesIds = gmbAddressesIds;
      this.selectedCities = cities;
      this.facilitiesGroupsIds = facilitiesGroupsIds;
      this.getStats();
      this.$refs.reviewsAnalystComparisonTable.getStatsTable();
    },

    setParams() {
      if (this.$route.query.dateFrom && this.$route.query.dateTo) {
        this.periods.first = [
          this.$route.query.dateFrom,
          this.$route.query.dateTo
        ];
      }
    },

    goToGMBAccounts() {
      this.$router.push({ name: "profile-gmb-accounts" });
    }
  }
};
</script>

<style lang="scss" scoped>
.filters {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
}

.summary {
  min-height: 460px;
}

.comparison {
  min-height: 300px;
}
</style>

<style lang="scss">
.reviews-analyst-widget {
  position: relative;
}

.reviews-filter-alert {
  border-radius: 8px;
  background: white;
  @include font-size(14px);
  padding: $spacing * 0.8 $spacing * 1.5;
}
</style>
